/**
 * Route capability guard.
 *
 * Extracted from main.js so the fail-open branches are directly testable — main.js is
 * the app entry and importing it runs the whole boot sequence, which means the one
 * behaviour most likely to cause a visible regression (a cold load with scope not yet
 * resolved) would otherwise be the one behaviour no test could reach.
 *
 * `deps` exists only for those tests. Production callers pass nothing.
 */
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { NavMetaManager } from '../../../../shared/meta/meta_nav.js';
import AuthController from '../services/AuthController.js';

/**
 * Toast + send them somewhere they can actually be. Replaced in tests.
 *
 * THE REDIRECT DEPENDS ON main.js:72 — `window.router = router;` — which sits 450+ lines
 * ABOVE the first router.on() registration, so it is always assigned by the time any
 * handler (and therefore this guard) runs. The `&& window.router` below is a guard
 * against a missing global, not a maybe.
 *
 * Called out because the failure is silent and the tests cannot see it: they assign
 * window.router themselves, so if that line ever moved below the registrations or into a
 * conditional, this would degrade to toast-with-no-navigation — the denied user stays on
 * the page they were denied — with every test still green.
 */
function defaultOnDeny(routePath, req) {
  console.warn(`[Router] Capability denied for ${routePath}`, {
    required: req.capability || req.capabilityAny,
  });
  PubSub.publish(AppBroadcast.UI_TOAST, {
    message: 'You do not have access to that page.',
    type: 'warning',
  });
  // Never redirect a denied /home into itself.
  if (routePath !== '/home' && window.router) window.router.navigate('/home');
}

/**
 * Per-route capability guard — the "may this person OPEN this page" question.
 *
 * checkRouteAccess() above answers a DIFFERENT question and must not be confused with
 * this one. It maps the path to an mvvLegit route token and asks whether the token is
 * permitted in the current AUTH STATE. mvvLegit knows a fixed set of eleven tokens and
 * `home_route` is the only general-purpose authenticated one — so every protected route
 * passing the literal 'home' is CORRECT and load-bearing, not a bug. Passing a real
 * route name would produce an unknown token and bounce the user off every page.
 *
 * What was missing is this: nothing ever asked whether the signed-in person holds the
 * capability the route requires. The symptom was worse than a missing gate, because the
 * pages fetched anyway and 403'd, and mvvLegit's interceptor treats ANY 403 as a dead
 * session — so a managed_client user clicking Team was signed out. See the
 * settings-notifications fix for the same failure one page over.
 *
 * AND ONE FAMILY OF ROUTES WHERE IT RESTRICTS NOBODY AT ALL. /analytics,
 * /analytics/traffic, /analytics/conversions and /analytics/ad-performance are gated on
 * view_stats and view_ad_metrics — both in tenancy.js's VIEW_ONLY set, so EVERY persona
 * holds them, down to a managed_client viewer. Those gates are real but
 * non-discriminating. For those four routes the server's owner-scoping is the ONLY thing
 * standing between tenants; a green route guard is not tenant isolation there. Whether
 * GA4 traffic and ad metrics warrant a narrower capability than magnet stats is an open
 * product decision, deliberately not made here.
 *
 * TWO DELIBERATE FAIL-OPEN CASES. This is a UX guard, not a security boundary — the
 * server is the boundary, and every scoped route there authorizes the target owner
 * rather than trusting the client:
 *
 *   REACHABILITY CAVEAT, ADDED 2026-08-24 AFTER TESTING. Branch (1) below describes a
 *   path users cannot currently reach. A tokened cold load of any deep route does not
 *   land on that route at all: mvvLegit permits only error/splash/offline in auth state 0,
 *   so it bounces to splash, and splash-route.js's init() then publishes ROUTE_NAV to
 *   'home' unconditionally whenever a token exists — the requested route is discarded and
 *   NO route handler runs. Confirmed in a fresh private window against #/team.
 *
 *   So today the guard is only ever reached via IN-APP navigation, by which point scope
 *   is resolved and branch (1) never fires. The reasoning below is still correct and the
 *   branch must stay — it is what makes a hard refresh safe once splash is fixed — but do
 *   NOT read it as describing live behaviour, and do not treat the AUTH_SCOPE_LOADED
 *   re-resolution, the deferred denial, or team-route.js's accidental ordering as things
 *   that happen today. They are contingent on splash preserving the requested route,
 *   which is tracked as its own item.
 *
 *   1. SCOPE NOT YET RESOLVED. main.js only awaits fetchScope() when an ACTIVE_ORG
 *      override exists; the common single-org cold load is fire-and-forget and
 *      router.resolve() runs immediately after, so getCapabilities() is [] at the first
 *      route decision.
 *
 *      THE SPECIFIC CONSTRAINT, because "fail open on a cold load" is not self-evidently
 *      right and the reason is not general caution: AuthController #doPing() (:878-881)
 *      still hard-signs-out on ANY 401/403, and mvvLegit's fetch interceptor does the
 *      same for any request. So a closed guard here does not degrade into a tidy
 *      "access denied" — it produces a SIGN-OUT on every cold load, which is the exact
 *      failure this whole change exists to remove. That signout-on-403 behaviour is
 *      UNPATCHED as of 2026-08-24, so this decision is load-bearing on it: if it is ever
 *      narrowed to genuinely-invalid sessions, revisit this branch — closed may then be
 *      affordable. Until then it is not.
 *
 *      The deferred decision is NOT dropped. main.js's AUTH_SCOPE_LOADED subscriber
 *      compares a scope signature (capabilities are in it) and calls
 *      reloadCurrentRoute() -> router.resolve(), which re-runs the handler and therefore
 *      re-runs this guard, now with scope. That subscriber was written to re-render
 *      persona-dependent views; this guard has since come to DEPEND on it, so
 *      route-capability.test.js asserts the wiring exists and routeCapability.test.js
 *      asserts the guard flips its answer across the scope transition.
 *   THERE IS A THIRD STATE, AND IT IS NOT FAIL-OPEN. During an org switch getScope()
 *   returns the PREVIOUS org's cached scope — not null — so branch (1) is not entered and
 *   this guard returns a confident answer computed from the wrong tenant's capabilities.
 *
 *   It is narrower than it looks: the org-switch handler (main.js) only calls
 *   setActiveOrg(); it does not navigate or reload, so during the in-flight window
 *   nothing resolves a route and this guard is never asked. The AUTH_SCOPE_LOADED
 *   subscriber then reloads once /me lands, and its signature includes activeOrg.guid AND
 *   capabilities — so a resolved-to-DIFFERENT-resolved transition is caught, not just
 *   null-to-resolved.
 *
 *   The residual is a manual click during that window: router.resolve() runs, scope is
 *   the old org's, and the answer is confidently wrong. Direction matters — switching
 *   DOWN (reseller -> managed client) carries the HIGHER capability set, so this allows,
 *   the page fetches, 403, sign-out. Switching up merely over-denies and self-corrects on
 *   the reload. Bounded to one round trip, but it fails toward the bad direction.
 *
 *   Deliberately NOT special-cased here: distinguishing "stale" from "current" needs an
 *   in-flight marker on AuthController, which is a change to the scope layer rather than
 *   to this guard. Worth doing with the interceptor narrowing, since both are about the
 *   same thing — a 403 that means "wrong org context", not "dead session".
 *
 *   AND NOTE: if that /me FAILS, fetchScope's error branches return the cached scope
 *   WITHOUT publishing AUTH_SCOPE_LOADED, so no reload happens and this guard keeps
 *   answering from the old org indefinitely while requests carry the new X-Active-Org
 *   header. That is a pre-existing scope-layer bug, not one this guard introduces.
 *
 *   2. ROUTE NOT IN THE NAV. /settings/integrations/:provider is parameterised and has
 *      no nav entry. "Not described" must not mean "denied", or adding a page without a
 *      nav item silently breaks it.
 *
 * WHY 20 CALL SITES AND NOT ONE ROUTER HOOK. Navigo 8.11.1 does expose a router-wide
 * hook (router.hooks({ before }), and match.route.path yields the registered path, so a
 * single dispatch point is genuinely available and would be immune to a mis-keyed call
 * site). It was tried and rejected on evidence:
 *
 *   meta_nav's `capability` is a VISIBILITY rule — "may this person see this nav item" —
 *   and at least one route is publicly reachable while carrying one. /templates is
 *   registered with NO checkRouteAccess ("PUBLIC on purpose: the gallery holds no tenant
 *   data and the website links here") yet sits in the nav under
 *   capabilityAny: [provision_magnets, manage_magnets]. A global hook would read that
 *   visibility rule as an authority rule and deny a public page to, among others, any
 *   signed-in reseller viewer.
 *
 * Guarding at the call sites applies the gate exactly where the codebase has already
 * decided authentication is required, and skips the public routes structurally rather
 * than by an exemption list that would need maintaining. The cost is that a 21st route
 * could be added without a guard, or with the wrong key — so route-capability.test.js
 * parses main.js and asserts, per handler, that a guard exists AND that its literal
 * matches the path the handler is registered at.
 *
 * On denial the user goes to /home, NOT to signin: they are legitimately signed in, and
 * bouncing them to signin is indistinguishable from the sign-out bug being fixed here.
 * Every channel/role in CAPABILITY_MATRIX holds view_dashboard, so /home is always
 * reachable — the self-redirect check below is belt-and-braces against that changing.
 */
export function requireRouteCapability(routePath, deps = {}) {
  const auth = deps.auth || AuthController;
  const nav = deps.nav || NavMetaManager;
  const onDeny = deps.onDeny || defaultOnDeny;
  // (1) scope unresolved — allow; AUTH_SCOPE_LOADED will re-resolve.
  if (!auth.getScope()) return true;

  // (2) unmapped route — no stated requirement.
  const req = nav.getRouteCapability(routePath);
  if (!req) return true;

  // Persona gate, mirroring the nav's rule: an entry listing uiModes belongs
  // to those personas only, fail-closed. Without this, a capability that two
  // personas share drags a persona-scoped page into the wrong workspace (a
  // saas admin holds manage_deliverables for their own deliverables, but
  // /questionnaires is a per-client reseller surface).
  if (Array.isArray(req.uiModes) && !req.uiModes.includes(auth.getUiMode?.())) {
    onDeny(routePath, req);
    return false;
  }
  if (!req.capability && !req.capabilityAny) return true;

  const caps = auth.getCapabilities();
  const ok = req.capability
    ? caps.includes(req.capability)
    : req.capabilityAny.some((c) => caps.includes(c));
  if (ok) return true;

  onDeny(routePath, req);
  return false;
}
